import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getProfessionById,
  getProfessionLabelNew,
  getSpecializationLabel,
  filterSpecializationsForProfession,
  getProfessionForSpecialization
} from '@/services/professions/getProfessionsNew';

/**
 * Check if a specialization label is valid for display
 * Filters out "OTHER", free text with slug prefixes, and other invalid values
 */
const isValidSpecializationLabel = (label: string | null | undefined): boolean => {
  if (!label) return false;
  const trimmed = label.trim();
  if (!trimmed) return false;
  
  const lower = trimmed.toLowerCase();
  
  // Filter out "other" variants
  if (['other', 'אחר', 'others', 'אחרים'].includes(lower)) return false;
  
  // Filter out slug:value patterns (e.g., "plumber:something")
  if (/^[a-z_-]+:/i.test(trimmed)) return false;
  
  // Filter out values that are just English slugs (likely not translated)
  // But allow normal English words/phrases
  if (/^[a-z_-]+$/.test(trimmed) && trimmed.includes('_')) return false;
  
  return true;
};

interface UseProfessionDisplayProps {
  // The profession IDs the professional has (UUIDs)
  professionIds?: string[];
  // The specialization IDs the professional has (UUIDs)
  specializationIds?: string[];
  // The profession being searched/displayed (UUID) - if provided, only show this profession
  searchedProfessionId?: string;
  // Legacy fields for backward compatibility
  profession?: string;
  mainProfession?: string;
}

interface ProfessionDisplayResult {
  // The profession label to display
  professionLabel: string;
  // The specialization labels to display (filtered if searchedProfessionId is provided)
  specializationLabels: string[];
  // The profession ID being displayed
  displayedProfessionId: string | null;
}

/**
 * Hook to get the correct profession and specializations to display
 * based on search context and professional data
 */
export const useProfessionDisplay = ({
  professionIds,
  specializationIds,
  searchedProfessionId,
  profession,
  mainProfession
}: UseProfessionDisplayProps): ProfessionDisplayResult => {
  // Labels follow the site language; Hebrew pages are unchanged
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return useMemo(() => {
    // Determine which profession to display
    let displayedProfessionId: string | null = null;
    let professionLabel = '';
    let specializationLabels: string[] = [];

    // If we have new profession_ids array
    if (professionIds && professionIds.length > 0) {
      if (searchedProfessionId && professionIds.includes(searchedProfessionId)) {
        // Display the searched profession
        displayedProfessionId = searchedProfessionId;
      } else if (searchedProfessionId) {
        // Check if searchedProfessionId is a specialization under one of the professions
        const profForSpec = getProfessionForSpecialization(searchedProfessionId);
        if (profForSpec && professionIds.includes(profForSpec.id)) {
          displayedProfessionId = profForSpec.id;
        } else {
          // Fallback to first profession
          displayedProfessionId = professionIds[0];
        }
      } else {
        // No search context - show first profession
        displayedProfessionId = professionIds[0];
      }
    }

    // Get profession label - try multiple approaches
    if (displayedProfessionId) {
      const label = getProfessionLabelNew(displayedProfessionId, lang);
      // Only use label if it's a real label (not the raw ID back)
      professionLabel = label !== displayedProfessionId ? label : '';
    }
    
    // If no label yet, try the legacy profession field (slug like 'intercom-keypad')
    if (!professionLabel && profession) {
      const profData = getProfessionById(profession);
      if (profData) {
        professionLabel = getProfessionLabelNew(profData.id, lang);
        // Also update displayedProfessionId to the correct UUID for filtering specializations
        if (!displayedProfessionId) {
          displayedProfessionId = profData.id;
        }
      }
    }
    
    // Final fallback: if we have mainProfession, try that
    if (!professionLabel && mainProfession) {
      const profData = getProfessionById(mainProfession);
      if (profData) {
        professionLabel = getProfessionLabelNew(profData.id, lang);
        if (!displayedProfessionId) {
          displayedProfessionId = profData.id;
        }
      }
    }

    // Get specialization labels - only from specialization_ids (no fallbacks)
    if (specializationIds && specializationIds.length > 0 && displayedProfessionId) {
      // Filter to only specializations belonging to the displayed profession
      const filteredSpecIds = filterSpecializationsForProfession(
        specializationIds,
        displayedProfessionId
      );
      
      specializationLabels = filteredSpecIds
        .map(id => getSpecializationLabel(id, lang))
        .filter(isValidSpecializationLabel)
        .slice(0, 3) as string[];
    }
    // If no specialization_ids - no sub-professions displayed

    return {
      professionLabel,
      specializationLabels,
      displayedProfessionId
    };
  }, [professionIds, specializationIds, searchedProfessionId, profession, mainProfession, lang]);
};
